import { useState, useEffect, useCallback } from 'react';
import { wechatAuthService, WechatUser, WechatAuthResult } from '../services/wechatAuth';

export interface UseWechatAuthReturn {
    // 状态
    isAuthenticated: boolean;
    user: WechatUser | null;
    isLoading: boolean;
    error: string | null;
    isWechatBrowser: boolean;
    
    // 方法
    authorize: () => Promise<void>;
    logout: () => Promise<void>;
    checkAuthStatus: () => Promise<boolean>;
    handleCallback: (code: string, state: string) => Promise<WechatAuthResult>;
    clearError: () => void;
    refreshToken: () => Promise<boolean>;
}

/**
 * 微信认证Hook
 * 提供微信公众号认证的状态管理和操作方法
 */
export const useWechatAuth = (): UseWechatAuthReturn => {
    const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
    const [user, setUser] = useState<WechatUser | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [isWechatBrowser] = useState<boolean>(wechatAuthService.isWechatBrowser());

    /**
     * 清除错误信息
     */
    const clearError = useCallback(() => {
        setError(null);
    }, []);

    /**
     * 更新认证状态
     */
    const updateAuthState = useCallback(() => {
        const authenticated = wechatAuthService.getIsAuthenticated();
        const currentUser = wechatAuthService.getCurrentUser();
        
        setIsAuthenticated(authenticated);
        setUser(currentUser);
    }, []);

    /**
     * 检查认证状态
     */
    const checkAuthStatus = useCallback(async (): Promise<boolean> => {
        try {
            setIsLoading(true);
            setError(null);
            
            const isValid = await wechatAuthService.checkAuthStatus();
            
            if (isValid) {
                updateAuthState();
                return true;
            } else {
                setIsAuthenticated(false);
                setUser(null);
                return false;
            }
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : '检查认证状态失败';
            setError(errorMessage);
            setIsAuthenticated(false);
            setUser(null);
            return false;
        } finally {
            setIsLoading(false);
        }
    }, [updateAuthState]);

    /**
     * 启动微信认证
     */
    const authorize = useCallback(async (): Promise<void> => {
        try {
            setIsLoading(true);
            setError(null);
            
            await wechatAuthService.authorize();
            // 注意：这里不会执行到，因为会跳转到微信授权页面
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : '启动认证失败';
            setError(errorMessage);
            setIsLoading(false);
        }
    }, []);

    /**
     * 处理微信回调
     */
    const handleCallback = useCallback(async (code: string, state: string): Promise<WechatAuthResult> => {
        try {
            setIsLoading(true);
            setError(null);
            
            const result = await wechatAuthService.handleCallback(code, state);
            
            if (result.success) {
                updateAuthState();
            } else {
                setError(result.error || '认证失败');
                setIsAuthenticated(false);
                setUser(null);
            }
            
            return result;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : '处理认证回调失败';
            setError(errorMessage);
            setIsAuthenticated(false);
            setUser(null);
            
            return {
                success: false,
                error: errorMessage
            };
        } finally {
            setIsLoading(false);
        }
    }, [updateAuthState]);

    /**
     * 登出
     */
    const logout = useCallback(async (): Promise<void> => {
        try {
            setIsLoading(true);
            setError(null);
            
            await wechatAuthService.logout();
            
            setIsAuthenticated(false);
            setUser(null);
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : '登出失败';
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * 刷新访问令牌
     */
    const refreshToken = useCallback(async (): Promise<boolean> => {
        try {
            setError(null);
            
            const success = await wechatAuthService.refreshToken();
            
            if (success) {
                updateAuthState();
            } else {
                setIsAuthenticated(false);
                setUser(null);
            }
            
            return success;
        } catch (err) {
            const errorMessage = err instanceof Error ? err.message : '刷新令牌失败';
            setError(errorMessage);
            setIsAuthenticated(false);
            setUser(null);
            return false;
        }
    }, [updateAuthState]);

    /**
     * 初始化认证状态
     */
    useEffect(() => {
        const initializeAuth = async () => {
            try {
                setIsLoading(true);
                
                // 首先从本地状态恢复
                updateAuthState();
                
                // 然后验证认证状态
                if (wechatAuthService.getIsAuthenticated()) {
                    await checkAuthStatus();
                } else {
                    setIsLoading(false);
                }
            } catch (err) {
                console.error('初始化认证状态失败:', err);
                setIsAuthenticated(false);
                setUser(null);
                setIsLoading(false);
            }
        };

        initializeAuth();
    }, [updateAuthState, checkAuthStatus]);

    /**
     * 监听页面可见性变化，自动检查认证状态
     */
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible' && isAuthenticated) {
                // 页面变为可见时，检查认证状态
                checkAuthStatus();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        
        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [isAuthenticated, checkAuthStatus]);

    /**
     * 定期检查认证状态（每30分钟）
     */
    useEffect(() => {
        if (!isAuthenticated) return;

        const interval = setInterval(() => {
            checkAuthStatus();
        }, 30 * 60 * 1000); // 30分钟

        return () => clearInterval(interval);
    }, [isAuthenticated, checkAuthStatus]);

    return {
        // 状态
        isAuthenticated,
        user,
        isLoading,
        error,
        isWechatBrowser,
        
        // 方法
        authorize,
        logout,
        checkAuthStatus,
        handleCallback,
        clearError,
        refreshToken
    };
};

export default useWechatAuth;
